import { Box } from '@chakra-ui/react';
import type {
  RichTextComponentsMarks,
  RichTextComponentsTypes,
  RichTextResolverProps,
} from '@time-line/components/common';
import {
  ArticleContainer,
  Blockquote,
  FootnoteAnnotation,
  LargeSerifContainer,
  Link,
  MixedContentContainer,
  ProductBenefitsContainer,
  ProductBulletPoints,
  RichTextResolver as RichTextResolverBase,
  SimpleTextContainer,
  SmallprintContainer,
  SmallTextContainer,
  SnugContentContainer,
  WrappedImage,
} from '@time-line/components/common';
import { ImageWithCaption } from 'components/blog/ImageWithCaption';
import { Video } from 'components/blog/Video';
import { ProductInArticle } from 'components/product/ProductInArticle';
import { getFootnotes } from 'lib/sanity';

import { PageFootnoteLink } from '../PageFootnote';

export {
  ArticleContainer,
  LargeSerifContainer,
  MixedContentContainer,
  ProductBenefitsContainer,
  ProductBulletPoints,
  SimpleTextContainer,
  SmallprintContainer,
  SmallTextContainer,
  SnugContentContainer,
};

export {
  AboutPageMissionContainer,
  LongevityDayContainer,
  SectionHeaderContainer,
} from './containers';

export const RichTextResolver = ({ ...props }: RichTextResolverProps) => {
  /* eslint-disable react/no-unstable-nested-components */
  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  /* eslint-disable @typescript-eslint/no-unsafe-assignment */

  const footnotes = getFootnotes(props.blocks);

  const marks: RichTextComponentsMarks = {
    timeline_footnote({ children, markKey }) {
      const footnoteIndex =
        footnotes.findIndex((footnote) => footnote._key === markKey) + 1;
      return (
        <FootnoteAnnotation indices={[footnoteIndex]}>
          {children}
        </FootnoteAnnotation>
      );
    },
    timeline_pageFootnoteAnnotation({ value, children }) {
      const { pageFootnotes } = value;
      return (
        <PageFootnoteLink pageFootnotes={pageFootnotes}>
          {children}
        </PageFootnoteLink>
      );
    },
    timeline_link({ children, value }) {
      return (
        <Link underline variant="unstyled" href={value.href}>
          {children}
        </Link>
      );
    },
    inlineImage({ value, children }) {
      const image = value?.image;
      if (!image) return <>{children}</>;

      // Replace the marked text with just the image
      return (
        <Box
          as="span"
          display="inline-block"
          height="1em"
          verticalAlign="middle"
          position="relative"
          top="-0.25em"
        >
          <WrappedImage
            image={image}
            wrapperProps={{
              as: 'span',
              display: 'inline-block',
              fontSize: 'inherit',
              lineHeight: 1,
            }}
            style={{
              width: 'auto',
              height: '1.25em',
            }}
          />
        </Box>
      );
    },
  };

  const types: RichTextComponentsTypes = {
    timeline_blogImage({ value }) {
      return <ImageWithCaption image={value.image} caption={value.caption} />;
    },
    timeline_blogVideo({ value }) {
      return <Video url={value.URL} title={value.title} />;
    },
    product({ value }) {
      return <ProductInArticle slug={value.slug} />;
    },
    timeline_blockQuote({ value }) {
      return <Blockquote quote={value.quote} attribution={value.attribution} />;
    },
  };

  return (
    <RichTextResolverBase
      {...props}
      blocks={props.blocks}
      types={types}
      marks={marks}
    />
  );
};
